/**
 * Dữ liệu giả của Cộng đồng/Chat — port từ SK_POSTS, SK_THREADS trong bản thiết kế.
 * Bật bằng VITE_SOCIAL_MOCK=true để chạy không cần API (mobile: EXPO_PUBLIC_SOCIAL_MOCK). Các hàm có cùng chữ ký
 * với communityApi/chatApi và giữ state trong bộ nhớ (like, bình luận, gửi tin...).
 * Chỉ import type từ src/api: file api import ngược lại file này.
 */
import type { ConversationDetailView, ConversationView, CreateGroupInput, MessageView, SendMessageInput, SuggestedGroupView } from '../api/chat.types';
import type { ImageUpload } from '../api/chat';
import type { CommentView, CreatePostInput, PostDetailView, PostType, SocialUser } from '../api/community.types';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const ago = (ms: number) => new Date(Date.now() - ms).toISOString();

function user(id: string, name: string, level: string, online = false): SocialUser {
  return { id, name, level, online };
}

export const MOCK_ME = user('u_me', 'Minh Anh', 'N5 · Unit 3', true);
const BAO = user('u_bao', 'Trần Quốc Bảo', 'N5 · Unit 2');
const YEN = user('u_yen', 'Nguyễn Hải Yến', 'N5 · Unit 3', true);
const LINH = user('u_linh', 'Đỗ Khánh Linh', 'N5 · Unit 4');
const HA = user('u_ha', 'Lê Thu Hà', 'N4', true);
const DUC = user('u_duc', 'Phạm Đức', 'N5');
const MINH = user('u_minh', 'Võ Minh', 'N5 · Unit 3');

let nextId = 1;
const uid = (prefix: string) => `${prefix}_${nextId++}`;

function comment(author: SocialUser, body: string, age: number, likeCount: number, isBest = false): CommentView {
  return { id: uid('cmt'), author, body, isBest, likeCount, liked: false, createdAt: ago(age) };
}

const posts: PostDetailView[] = [
  {
    id: 'p1',
    type: 'QA',
    customTopic: null,
    author: BAO,
    body: 'Mọi người cho mình hỏi: khi nào dùng は và khi nào dùng が? Mình đọc giải thích rồi mà vẫn hay nhầm.',
    attachment: { kind: 'jp', text: '私は学生です。 / 猫がいます。', vi: 'Tôi là học sinh. / Có con mèo.' },
    solved: true,
    likeCount: 14,
    liked: false,
    commentCount: 2,
    createdAt: ago(12 * MINUTE),
    comments: [
      comment(HA, 'Dễ nhớ nhất: は giới thiệu chủ đề đang nói tới, が nhấn vào chủ thể mới xuất hiện. Câu "có con mèo" là thông tin mới nên dùng が.', 8 * MINUTE, 9, true),
      comment(DUC, 'Bài Ngữ pháp G-Unit 3 có phần so sánh は/が, bạn xem lại nhé.', 5 * MINUTE, 2),
    ],
  },
  {
    id: 'p2',
    type: 'FIND_BUDDY',
    customTopic: null,
    author: YEN,
    body: 'Tìm 3–4 bạn học Từ vựng Unit 3 buổi tối (20:00–21:00). Mỗi tối ôn 20 thẻ SRS rồi chat kiểm tra nhau 15 phút.',
    attachment: { kind: 'group', conversationId: 'g_toinay', name: 'Tối nay học gì', mark: '夜', members: 2, max: 5, schedule: '20:00 mỗi tối', joined: false },
    solved: false,
    likeCount: 8,
    liked: true,
    commentCount: 1,
    createdAt: ago(HOUR),
    comments: [comment(MINH, 'Cho mình tham gia với, mình cũng đang Unit 3.', 40 * MINUTE, 1)],
  },
  {
    id: 'p3',
    type: 'ACHIEVEMENT',
    customTopic: null,
    author: LINH,
    body: 'Hoàn thành hết bảng Hiragana sau 3 tuần. Mẹo của mình: mỗi ngày viết lại 5 chữ sai nhiều nhất trước khi ngủ.',
    attachment: { kind: 'streak', streak: 21, xp: 1240 },
    solved: false,
    likeCount: 42,
    liked: false,
    commentCount: 0,
    createdAt: ago(3 * HOUR),
    comments: [],
  },
  {
    id: 'p4',
    type: 'SHARE',
    customTopic: null,
    author: HA,
    body: 'Chia sẻ bộ thẻ "Đồ ăn trong konbini" mình tự làm — 24 từ, có ví dụ.',
    attachment: { kind: 'deck', name: 'Đồ ăn trong konbini', count: 24, sample: 'おにぎり · 弁当 · 飲み物' },
    solved: false,
    likeCount: 31,
    liked: false,
    commentCount: 0,
    createdAt: ago(26 * HOUR),
    comments: [],
  },
];

function notFound(): never {
  throw new Error('Không tìm thấy dữ liệu giả');
}

function findPost(postId: string) {
  return posts.find((p) => p.id === postId) ?? notFound();
}

function findComment(commentId: string) {
  for (const p of posts) {
    const c = p.comments.find((x) => x.id === commentId);
    if (c) return { post: p, comment: c };
  }
  return notFound();
}

function toggleLike(target: { liked: boolean; likeCount: number }, liked: boolean) {
  if (target.liked !== liked) {
    target.liked = liked;
    target.likeCount += liked ? 1 : -1;
  }
  return { liked, likeCount: target.likeCount };
}

export const mockCommunityApi = {
  getMe: async () => MOCK_ME,
  getFeed: async (type?: PostType) => ({
    items: posts.filter((p) => !type || p.type === type).map((p) => ({ ...p })),
    nextCursor: null,
  }),
  getPost: async (postId: string) => ({ ...findPost(postId), comments: [...findPost(postId).comments] }),
  createPost: async (input: CreatePostInput) => {
    const post: PostDetailView = {
      id: uid('p'),
      type: input.type,
      customTopic: input.customTopic ?? null,
      author: MOCK_ME,
      body: input.body,
      attachment: input.attachment ?? null,
      solved: false,
      likeCount: 0,
      liked: false,
      commentCount: 0,
      createdAt: new Date().toISOString(),
      comments: [],
    };
    posts.unshift(post);
    return post;
  },
  deletePost: async (postId: string) => {
    posts.splice(posts.indexOf(findPost(postId)), 1);
    return { deleted: true as const };
  },
  likePost: async (postId: string) => toggleLike(findPost(postId), true),
  unlikePost: async (postId: string) => toggleLike(findPost(postId), false),
  addComment: async (postId: string, body: string) => {
    const post = findPost(postId);
    const added = comment(MOCK_ME, body, 0, 0);
    post.comments.push(added);
    post.commentCount += 1;
    return added;
  },
  deleteComment: async (commentId: string) => {
    const { post, comment: c } = findComment(commentId);
    post.comments.splice(post.comments.indexOf(c), 1);
    post.commentCount -= 1;
    return { deleted: true as const };
  },
  likeComment: async (commentId: string) => toggleLike(findComment(commentId).comment, true),
  unlikeComment: async (commentId: string) => toggleLike(findComment(commentId).comment, false),
  toggleBestComment: async (commentId: string) => {
    const { post, comment: c } = findComment(commentId);
    const isBest = !c.isBest;
    post.comments.forEach((x) => (x.isBest = false));
    c.isBest = isBest;
    post.solved = isBest;
    return { isBest, solved: isBest };
  },
  reportPost: async () => ({ reported: true as const }),
};

function today(hours: number, minutes: number) {
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

function msg(conversationId: string, sender: SocialUser, text: string, createdAt: string, extra: Partial<MessageView> = {}): MessageView {
  return {
    id: uid('msg'),
    conversationId,
    senderId: sender.id,
    senderName: sender.name,
    text,
    isJapanese: false,
    sharedCard: null,
    image: null,
    translation: null,
    createdAt,
    ...extra,
  };
}

function conversation(c: Partial<ConversationDetailView> & Pick<ConversationDetailView, 'id' | 'kind' | 'name'>): ConversationDetailView {
  return {
    mark: null,
    focus: null,
    weeklyGoal: null,
    isPublic: false,
    memberCount: 2,
    onlineCount: 0,
    peer: null,
    unread: 0,
    lastMessage: null,
    lastMessageAt: null,
    goalProgress: null,
    members: [MOCK_ME],
    ...c,
  };
}

const threads: { conversation: ConversationDetailView; messages: MessageView[] }[] = [
  {
    conversation: conversation({
      id: 't1',
      kind: 'GROUP',
      name: 'N5 cùng tiến',
      mark: '五',
      memberCount: 6,
      onlineCount: 3,
      unread: 3,
      weeklyGoal: 150,
      goalProgress: 96,
      isPublic: true,
      members: [MOCK_ME, YEN, HA, MINH, BAO, LINH],
    }),
    messages: [
      msg('t1', MINH, 'Mình xong 20 thẻ rồi, sai 3 thẻ toàn từ có っ.', today(20, 2)),
      msg('t1', YEN, 'Ai muốn kiểm tra chéo không? Mình hỏi nghĩa, các bạn trả lời bằng tiếng Nhật.', today(20, 10)),
      msg('t1', YEN, '', today(20, 11), { sharedCard: { learningItemId: 'item_kitte', jp: 'きって', vi: 'con tem', kind: 'Thẻ từ vựng · V-Unit 3' } }),
      msg('t1', YEN, 'Tối nay mọi người ôn xong 20 thẻ chưa?', today(20, 14)),
    ],
  },
  {
    conversation: conversation({ id: 't2', kind: 'DM', name: HA.name, peer: HA, onlineCount: 1, unread: 1, members: [MOCK_ME, HA] }),
    messages: [
      msg('t2', MOCK_ME, 'Cảm ơn bạn đã giải thích は/が, mình hiểu hơn nhiều rồi.', today(19, 31)),
      msg('t2', HA, 'ありがとう！Mai mình gửi thêm ví dụ nhé.', today(19, 40), { isJapanese: true, translation: 'Cảm ơn!' }),
    ],
  },
  {
    conversation: conversation({ id: 't3', kind: 'GROUP', name: 'Luyện nghe mỗi sáng', mark: '聴', memberCount: 12, members: [MOCK_ME, DUC, BAO] }),
    messages: [msg('t3', MOCK_ME, 'Mình nghe bài 4 rồi, đoạn 2 khó quá.', ago(26 * HOUR))],
  },
  {
    conversation: conversation({ id: 't4', kind: 'DM', name: BAO.name, peer: BAO, members: [MOCK_ME, BAO] }),
    messages: [msg('t4', BAO, 'Ok bạn, hẹn tối thứ 4 nhé.', ago(4 * 24 * HOUR))],
  },
];

function withLastMessage(t: (typeof threads)[number]): ConversationDetailView {
  const last = t.messages[t.messages.length - 1];
  return {
    ...t.conversation,
    lastMessage: last ? { senderId: last.senderId, senderName: last.senderName, text: last.text, hasImage: !!last.image } : null,
    lastMessageAt: last?.createdAt ?? null,
  };
}

function findThread(conversationId: string) {
  return threads.find((t) => t.conversation.id === conversationId) ?? notFound();
}

const suggested: SuggestedGroupView[] = [{ id: 'g_kanji', name: 'Kanji N5 mỗi ngày 3 chữ', mark: '字', memberCount: 18, focus: 'đang ở Unit 3' }];

const CONTACTS = [HA, BAO, YEN, MINH, LINH];

const unreadTotal = () => threads.reduce((sum, t) => sum + t.conversation.unread, 0);

export const mockChatApi = {
  listConversations: async (): Promise<ConversationView[]> =>
    threads.map(withLastMessage).sort((a, b) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? '')),
  getConversation: async (conversationId: string) => withLastMessage(findThread(conversationId)),
  getMessages: async (conversationId: string) => ({
    items: [...findThread(conversationId).messages].reverse(),
    nextCursor: null,
  }),
  sendMessage: async (conversationId: string, input: SendMessageInput) => {
    const created = msg(conversationId, MOCK_ME, input.text ?? '', new Date().toISOString(), {
      isJapanese: /[ぁ-ヶ一-龠]/.test(input.text ?? ''),
      sharedCard: input.sharedCard ?? null,
    });
    findThread(conversationId).messages.push(created);
    return created;
  },
  sendImage: async (conversationId: string, image: ImageUpload) => {
    const created = msg(conversationId, MOCK_ME, '', new Date().toISOString(), {
      image: { path: URL.createObjectURL(image.blob), width: image.width, height: image.height },
    });
    findThread(conversationId).messages.push(created);
    return created;
  },
  markRead: async (conversationId: string) => {
    findThread(conversationId).conversation.unread = 0;
    return { unread: unreadTotal() };
  },
  getUnread: async () => ({ unread: unreadTotal() }),
  openDm: async (userId: string) => {
    const existing = threads.find((t) => t.conversation.peer?.id === userId);
    if (existing) return withLastMessage(existing);
    const peer = [BAO, YEN, LINH, HA, DUC, MINH].find((u) => u.id === userId) ?? notFound();
    const created = { conversation: conversation({ id: uid('t'), kind: 'DM', name: peer.name, peer, onlineCount: peer.online ? 1 : 0, members: [MOCK_ME, peer] }), messages: [] };
    threads.push(created);
    return withLastMessage(created);
  },
  getSuggestedGroups: async () => suggested,
  createGroup: async (input: CreateGroupInput) => {
    const created = {
      conversation: conversation({
        id: uid('t'),
        kind: 'GROUP',
        name: input.name,
        mark: input.name.charAt(0).toUpperCase(),
        focus: input.focus,
        weeklyGoal: input.weeklyGoal,
        goalProgress: 0,
        isPublic: input.isPublic,
        memberCount: input.memberIds.length + 1,
        onlineCount: 1,
        members: [MOCK_ME, ...CONTACTS.filter((u) => input.memberIds.includes(u.id))],
      }),
      messages: [],
    };
    threads.push(created);
    return withLastMessage(created);
  },
  joinGroup: async (conversationId: string) => {
    const existing = threads.find((t) => t.conversation.id === conversationId);
    if (existing) return withLastMessage(existing);
    const group = suggested.find((g) => g.id === conversationId);
    const created = {
      conversation: conversation({ id: conversationId, kind: 'GROUP', name: group?.name ?? 'Tối nay học gì', mark: group?.mark ?? '夜', memberCount: (group?.memberCount ?? 2) + 1, isPublic: true }),
      messages: [],
    };
    threads.push(created);
    if (group) suggested.splice(suggested.indexOf(group), 1);
    return withLastMessage(created);
  },
  getContacts: async () => CONTACTS,
};
